
import { defineComponent, ref, reactive, toRefs, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import f10ApiHk from '@/api/f10ApiHk'
import * as echarts from 'echarts'

export default defineComponent({
  components: { Charts, Nodata },
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: ''
    }
  },
  setup(props: any) {
    const route = useRoute()
    const state = reactive({
      option: {} as echarts.EChartsOption,
      spinning: true,
      data: [] as any[]
    })

    onMounted(() => {
      const data = {
        data: {
          // market: 'HK',
          // symbol: '00700'
          market: route.query.market || 'HK',
          symbol: route.query.code || '00700',
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
        }
      }

      f10ApiHk.getBriefStockDividend(data).then((res: any) => {
        if (res.data?.stockDividend?.list) {
          state.data = res.data.stockDividend.list
          generateChartOption()
        }
        state.spinning = false
      }).catch(() => {
        state.spinning = false
      })
    })

    const generateChartOption = () => {
      // 提取近3年数据（2022-2024）
      const recentData = state.data
        // .filter(item => item.fiscalYear >= 2022)
        .sort((a, b) => a.fiscalYear - b.fiscalYear)

      const years = recentData.map(item => item.fiscalYear.toString())
      const npData = recentData.map(item => {
        const value = item.npParentCompanyOwners
        return value !== null && value !== undefined ? (value / 100000000).toFixed(2) : '0'
      })
      const dividendData = recentData.map(item => {
        const value = item.cashDividendsTotalLc
        return value !== null && value !== undefined ? (value / 100000000).toFixed(2) : '0'
      })
      const ratioData = recentData.map(item => {
        const value = item.dividendPaidRatio
        return value !== null && value !== undefined ? (value * 100).toFixed(2) : '0'
      })

      state.option = {
        backgroundColor: props.theme === 'dark' ? '#1f1f1f' : '#fff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          },
          formatter: (params: any) => {
            const year = params[0].name
            const dataItem = recentData.find((item: any) => item.fiscalYear.toString() === year)
            if (!dataItem) return ''

            // 格式化数值，自动选择单位（亿或万）
            const formatValue = (value: number | null | undefined) => {
              if (value === null || value === undefined) return '0'
              const absValue = Math.abs(value)
              if (absValue >= 100000000) {
                return (value / 100000000).toFixed(2) + '亿'
              } else if (absValue >= 10000) {
                return (value / 10000).toFixed(2) + '万'
              } else {
                return value.toFixed(2)
              }
            }

            const npValue = formatValue(dataItem.npParentCompanyOwners)
            const dividendValue = formatValue(dataItem.cashDividendsTotalLc)
            const ratioValue = dataItem.dividendPaidRatio !== null && dataItem.dividendPaidRatio !== undefined
              ? (dataItem.dividendPaidRatio * 100).toFixed(2) : '0'
            const cntValue = dataItem.cashDividendCnt !== null && dataItem.cashDividendCnt !== undefined
              ? dataItem.cashDividendCnt : '0'

            return `
              <div style="margin: 5px 0;font-weight:bold">${year}财年</div>
              <div>归母净利润: ${npValue}</div>
              <div>派现金额: ${dividendValue}</div>
              <div>股利支付率: ${ratioValue}%</div>
              <div>派息次数: ${cntValue}次</div>
            `
          }
        },
        legend: {
          data: ['归母净利润', '派现金额', '股利支付率', '派息次数'],
          textStyle: {
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
          },
          bottom: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '9%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          axisLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            }
          },
          axisLabel: {
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '金额 (亿元)',
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            axisLabel: {
              formatter: '{value}',
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#2a2a2a' : '#eee'
              }
            }
          },
          {
            type: 'value',
            name: '股利支付率 (%)',
            // 移除 min: 0, max: 100 限制，允许显示负值
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            axisLabel: {
              formatter: '{value}%',
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '归母净利润',
            type: 'bar',
            barWidth: '25%',
            itemStyle: {
              color: '#5470c6'
            },
            data: npData
          },
          {
            name: '派现金额',
            type: 'bar',
            barWidth: '25%',
            itemStyle: {
              color: '#91cc75'
            },
            data: dividendData
          },
          {
            name: '股利支付率',
            type: 'line',
            yAxisIndex: 1,
            symbolSize: 8,
            itemStyle: {
              color: '#fc8452'
            },
            lineStyle: {
              width: 3
            },
            data: ratioData
          }
        ]
      }
    }

    return {
      ...toRefs(state)
    }
  }
})
